export const upperPowerOf2 = (n: number) => {
  const result = 1 << (31 - Math.clz32(n));
  return result === n ? result : 2 * result;
};

export const maxAnnotationTextureWidth = 4096;

export const calcTextureSize = (maxId: number) => {
  // Most WebGL devices support 4096x4096 image == 16M pixels
  const maxImageWidth = maxAnnotationTextureWidth;
  // can be extended using two or more textures
  const maxIdLimit = maxImageWidth * maxImageWidth;

  if (maxId >= maxIdLimit) {
    throw new Error('Point attribute maxId oveflow');
  }

  return maxId <= maxImageWidth
    ? { height: 1, width: upperPowerOf2(maxId + 1) }
    : { width: maxImageWidth, height: upperPowerOf2(Math.ceil((maxId + 1) / maxImageWidth)) };
};

export class PointsAttributeBuffer {
  public maxId: number;

  private numberPoints: number;

  private textureSize: { width: number; height: number };

  public cardinality: number;

  public data: Float32Array;

  constructor(maxId: number, numberPoints: number) {
    this.maxId = maxId;
    this.numberPoints = numberPoints;
    this.textureSize = calcTextureSize(maxId);

    const { height } = this.textureSize;
    this.cardinality = height === 1 ? 1 : 2;

    this.data = new Float32Array(numberPoints * this.cardinality);
  }

  public setValue(pointIndex: number, id: number) {
    if (this.cardinality === 1) {
      this.data[pointIndex] = this.annotationIdToU(id);
    } else {
      const { u, v } = this.annotationIdToUV(id);
      this.data[pointIndex * 2 + 0] = u;
      this.data[pointIndex * 2 + 1] = v;
    }
  }

  public getValue(pointIndex: number) {
    if (this.cardinality === 1) {
      const u = this.data[pointIndex];
      return this.uToAnnotationId(u);
    }

    const u = this.data[pointIndex * 2 + 0];
    const v = this.data[pointIndex * 2 + 1];

    return this.uvToAnnotationId(u, v);
  }

  private annotationIdToUV(annotationId: number) {
    // Annotation id splitted into parts u,v for 2d-texture lookup
    // Annotation id color update should change corresponding u,v pixel of texture
    const { width, height } = this.textureSize;

    const x = Math.floor(annotationId % width);
    const y = Math.floor(annotationId / width);
    const u = (x + 0.5) / width;
    const v = (y + 0.5) / height;
    return { u, v };
  }

  private annotationIdToU(annotationId: number) {
    // Using single dimention (width) for small number of ids/colors <= 65536
    const { width } = this.textureSize;
    const x = annotationId;
    const u = (x + 0.5) / width;
    return u;
  }

  private uToAnnotationId(u: number) {
    const { width } = this.textureSize;
    return Math.round(u * width - 0.5);
  }

  private uvToAnnotationId(u: number, v: number) {
    const { width, height } = this.textureSize;

    const x = Math.round(u * width - 0.5);
    const y = Math.round(v * height - 0.5);

    return x + y * width;
  }
}

export default PointsAttributeBuffer;
